import { Link } from "react-router-dom";
import { ClipboardList, ShieldCheck } from "lucide-react";

const sections = [
  {
    id: "overview",
    title: "Overview",
    content: [
      {
        type: "text",
        text: "Hoardexa (\"we\", \"our\", or \"us\"), a product of Solucate, is an outdoor advertising management platform built for hoarding inventory control, campaign management, client tracking, and operational reporting. This Privacy Policy explains what personal data we collect, why we collect it, how we use and protect it, and the rights you have over your data.",
      },
      {
        type: "text",
        text: "By using Hoardexa — whether as an advertiser admin, employee, or a visitor submitting a contact request — you agree to the practices described in this policy.",
      },
    ],
  },
  {
    id: "gdpr",
    title: "GDPR Compliance",
    badge: true,
    content: [
      {
        type: "highlight",
        text: "Hoardexa is GDPR Compliant. We are committed to protecting the personal data of all users and contacts in accordance with the General Data Protection Regulation (EU) 2016/679.",
      },
      {
        type: "text",
        text: "What GDPR compliance means specifically for Hoardexa — an outdoor advertising management platform:",
      },
      {
        type: "list",
        items: [
          {
            title: "Lawful basis for processing",
            detail: "All personal data we process has a documented lawful basis: performance of a contract (managing your advertiser account, campaigns, and clients), legitimate interests (platform security, analytics, operational continuity), or explicit consent (contact form submissions and marketing communication).",
          },
          {
            title: "Data minimisation",
            detail: "We collect only the data needed to operate the platform. Hoarding records, campaign dates, client contact details, and employee accounts contain only the fields required for outdoor advertising management — no surplus personal profiling.",
          },
          {
            title: "Transparency",
            detail: "This Privacy Policy clearly explains every category of data we collect, why we collect it, and who has access to it. Users are notified at the point of data collection.",
          },
          {
            title: "Right of access",
            detail: "You can request a full export of your personal data held within Hoardexa at any time through the Contact us section on the home page.",
          },
          {
            title: "Right to rectification",
            detail: "If any personal data we hold about you is inaccurate or incomplete, you have the right to have it corrected. Advertiser admins can update profile and account data directly from the platform.",
          },
          {
            title: "Right to erasure (\"right to be forgotten\")",
            detail: "You may request deletion of your personal data. We will action erasure requests within 30 days, subject to any legal or contractual retention obligations.",
          },
          {
            title: "Right to data portability",
            detail: "You can request your personal data in a structured, machine-readable format (JSON or CSV) for transfer to another service provider.",
          },
          {
            title: "Right to restrict or object to processing",
            detail: "You may request that we restrict processing of your data or object to processing carried out on the basis of legitimate interests.",
          },
          {
            title: "Role-based access control",
            detail: "Hoardexa enforces granular permissions — advertiser admins, employees, and super admins each see only the data their role requires. This is a core GDPR data protection by design principle.",
          },
          {
            title: "Data security by design",
            detail: "All data is encrypted in transit (HTTPS/TLS). Passwords are hashed and never stored in plaintext. Access controls, audit trails, and session management are built into the platform architecture.",
          },
          {
            title: "Data retention policy",
            detail: "We retain account and campaign data for the duration of your active subscription and up to 12 months after account closure to meet legal and contractual obligations. Contact form data is retained for up to 6 months.",
          },
          {
            title: "Third-party processors",
            detail: "We use GDPR-compliant sub-processors: Cloudinary (image and media storage) and EmailJS (transactional email delivery). No personal data is sold or shared with third parties for advertising purposes.",
          },
          {
            title: "Breach notification",
            detail: "In the event of a personal data breach that poses a risk to your rights, we will notify the relevant supervisory authority within 72 hours and affected individuals without undue delay.",
          },
          {
            title: "Data controller",
            detail: "Solucate is the data controller for data processed through Hoardexa. You can reach the data controller through the Contact us section on the home page for any GDPR-related request.",
          },
        ],
      },
    ],
  },
  {
    id: "data-collected",
    title: "Data We Collect",
    content: [
      {
        type: "subheading",
        text: "Account and user data",
      },
      {
        type: "text",
        text: "When an advertiser account is created, we collect: full name, email address, company/agency name, contact number, and a hashed password. Employee accounts created within an advertiser workspace follow the same structure with an assigned role.",
      },
      {
        type: "subheading",
        text: "Operational platform data",
      },
      {
        type: "text",
        text: "Hoardexa stores the data you enter to run your outdoor advertising operations: hoarding inventory records (location, dimensions, photos, status), campaign records (client, dates, amounts), client contact details (name, company, email, phone), and booking or renewal history.",
      },
      {
        type: "subheading",
        text: "Contact form submissions",
      },
      {
        type: "text",
        text: "When you submit a demo request or contact us via the website, we collect your name, email address, agency name, and message. This data is used solely to respond to your enquiry.",
      },
      {
        type: "subheading",
        text: "Usage and log data",
      },
      {
        type: "text",
        text: "We collect standard server logs including IP address, browser type, pages visited, and timestamps for platform security and performance monitoring. This data is not linked to personal identities for marketing purposes.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "How We Use Your Data",
    content: [
      {
        type: "list-simple",
        items: [
          "Provision and operation of the Hoardexa platform and your account",
          "Enabling team collaboration through employee account management and role-based permissions",
          "Sending transactional emails: account verification, password resets, and campaign renewal notifications",
          "Generating analytics, insights, and reports on your own inventory and campaign data",
          "Responding to demo requests and support enquiries submitted via the contact form",
          "Ensuring platform security, detecting and preventing fraud or abuse",
          "Complying with legal obligations",
        ],
      },
    ],
  },
  {
    id: "data-sharing",
    title: "Data Sharing and Third Parties",
    content: [
      {
        type: "text",
        text: "We do not sell, rent, or share your personal data with third parties for their own marketing or commercial purposes. Data is shared only with the following sub-processors strictly required to deliver the platform:",
      },
      {
        type: "table",
        rows: [
          ["Cloudinary", "Image and media storage for hoarding photos", "GDPR-compliant; Standard Contractual Clauses in place"],
          ["EmailJS", "Transactional email delivery", "GDPR-compliant processor"],
          ["PostgreSQL hosting provider", "Database infrastructure", "Encrypted storage, access-controlled"],
        ],
        headers: ["Processor", "Purpose", "Safeguard"],
      },
    ],
  },
  {
    id: "security",
    title: "Data Security",
    content: [
      {
        type: "text",
        text: "We apply the following technical and organisational measures to protect your personal data:",
      },
      {
        type: "list-simple",
        items: [
          "All data in transit is encrypted using TLS (HTTPS)",
          "Passwords are stored using strong one-way hashing algorithms — never in plaintext",
          "Role-based access control ensures employees see only data relevant to their permissions",
          "Database access is restricted to application-level authenticated connections",
          "Media files (hoarding photos) are stored in a secured Cloudinary environment with signed access URLs",
          "Regular security reviews of authentication flows and data access patterns",
        ],
      },
    ],
  },
  {
    id: "your-rights",
    title: "Your Rights",
    content: [
      {
        type: "text",
        text: "Under GDPR and applicable data protection law, you have the following rights:",
      },
      {
        type: "list-simple",
        items: [
          "Access — request a copy of the personal data we hold about you",
          "Rectification — request correction of inaccurate or incomplete data",
          "Erasure — request deletion of your personal data (\"right to be forgotten\")",
          "Portability — receive your data in a structured, machine-readable format",
          "Restriction — request that we limit how we process your data",
          "Objection — object to processing based on legitimate interests",
          "Withdraw consent — where processing is based on consent, withdraw it at any time",
          "Lodge a complaint — with the relevant supervisory authority in your jurisdiction",
        ],
      },
      {
        type: "text",
        text: "To exercise any of these rights, use the Contact us section on the home page. We will respond within 30 days.",
      },
    ],
  },
  {
    id: "retention",
    title: "Data Retention",
    content: [
      {
        type: "text",
        text: "We retain personal data only for as long as necessary to fulfil the purpose for which it was collected:",
      },
      {
        type: "list-simple",
        items: [
          "Active account data: retained for the duration of your subscription",
          "Campaign and operational records: retained for up to 12 months after account closure",
          "Contact form data: retained for up to 6 months after initial enquiry",
          "Server logs: retained for up to 90 days for security monitoring",
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookies",
    content: [
      {
        type: "text",
        text: "Hoardexa uses session cookies strictly necessary for authentication and platform functionality. We do not use third-party advertising or tracking cookies. You can disable cookies in your browser settings, though this will prevent you from remaining logged into the platform.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    content: [
      {
        type: "text",
        text: "We may update this Privacy Policy from time to time. When we make material changes, we will notify active users via email or an in-platform notice. The \"Last updated\" date at the top of this page will always reflect the current version.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact and Data Controller",
    content: [
      {
        type: "text",
        text: "Solucate is the data controller for personal data processed through Hoardexa. For any privacy-related questions, GDPR data subject requests, or to report a data protection concern, contact us directly:",
      },
      {
        type: "contact",
        label: "Hoardexa / Solucate — Data Controller",
      },
    ],
  },
];

function NavbarSimple() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-700/25">
            <span className="font-display text-lg font-black leading-none">H</span>
          </div>
          <div className="text-left">
            <div className="font-display text-lg font-black leading-none text-slate-950">Hoardexa</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">
              Outdoor Ads
            </div>
          </div>
        </Link>
        <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
          Sign in
        </Link>
      </div>
    </header>
  );
}

function FooterSimple() {
  return (
    <footer className="bg-slate-950 py-8 text-white">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row lg:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-slate-950">
            <ClipboardList size={16} />
          </div>
          <div>
            <div className="font-display text-sm font-bold">Hoardexa</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Product by SkillEdge Technologies</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/privacy-policy" className="text-white">Privacy Policy</Link>
          <Link to="/careers" className="hover:text-white">Careers</Link>
          <Link to="/login" className="hover:text-white">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

function renderContent(block, idx) {
  if (block.type === "text") {
    return (
      <p key={idx} className="mt-4 text-base leading-7 text-slate-600">
        {block.text}
      </p>
    );
  }

  if (block.type === "highlight") {
    return (
      <div key={idx} className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-4">
        <p className="text-base font-bold leading-7 text-emerald-900">
          {block.text}
        </p>
      </div>
    );
  }

  if (block.type === "subheading") {
    return (
      <h3 key={idx} className="mt-6 font-display text-lg font-bold text-slate-950">
        {block.text}
      </h3>
    );
  }

  if (block.type === "list") {
    return (
      <div key={idx} className="mt-5 space-y-4">
        {block.items.map((item) => (
          <div key={item.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4 transition hover:border-blue-200">
            <div className="font-display text-sm font-bold text-slate-950">{item.title}</div>
            <p className="mt-1 text-sm leading-6 text-slate-600">{item.detail}</p>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "list-simple") {
    return (
      <ul key={idx} className="mt-4 space-y-2">
        {block.items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-sm leading-6 text-slate-600">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "table") {
    return (
      <div key={idx} className="mt-4 overflow-hidden rounded-lg border border-slate-200">
        <div className="grid grid-cols-3 bg-slate-950 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-300">
          {block.headers.map((h) => <div key={h}>{h}</div>)}
        </div>
        {block.rows.map((row, i) => (
          <div key={i} className={`grid grid-cols-3 gap-4 px-4 py-3 text-sm ${i % 2 ? "bg-white" : "bg-slate-50"}`}>
            {row.map((cell, j) => (
              <div key={j} className={j === 0 ? "font-semibold text-slate-950" : "text-slate-600"}>
                {cell}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "contact") {
    return (
      <div key={idx} className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{block.label}</div>
        <a href="mailto:contact@hoardexa.com" className="mt-1 block font-display text-base font-bold text-blue-600 transition hover:text-blue-700 hover:underline">
          contact@hoardexa.com
        </a>
      </div>
    );
  }

  return null;
}

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <NavbarSimple />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-[#f8fafc] pt-28 pb-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-emerald-700">
            <ShieldCheck size={13} />
            GDPR Compliant
          </div>
          <h1 className="mt-4 font-display text-5xl font-extrabold leading-[1.08] text-slate-950">
            Privacy Policy
          </h1>
          <p className="mt-4 text-base leading-7 text-slate-600">
            Last updated: June 2026 &nbsp;·&nbsp; Effective immediately for all Hoardexa users.
          </p>
          <p className="mt-3 text-sm font-semibold text-slate-700">
            <strong>Hoardexa is GDPR Compliant.</strong> We take your data protection rights seriously. This page explains everything about how your personal data is handled within our platform.
          </p>
        </div>
      </section>

      {/* TOC + Content */}
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Table of Contents */}
        <nav className="mb-12 rounded-lg border border-slate-200 bg-slate-50 p-5">
          <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">Contents</div>
          <ol className="grid gap-1 sm:grid-cols-2">
            {sections.map((s, i) => (
              <li key={s.id} className="rounded-md px-1 py-0.5 transition hover:bg-white">
                <a
                  href={`#${s.id}`}
                  className="text-sm font-medium text-slate-700 transition hover:text-blue-600"
                >
                  {i + 1}. {s.title}
                  {s.badge && <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"><ShieldCheck size={9} />GDPR</span>}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="space-y-14">
          {sections.map((section) => (
            <section key={section.id} id={section.id}>
              <div className="flex items-center gap-3">
                <h2 className="font-display text-2xl font-bold text-slate-950">{section.title}</h2>
                {section.badge && (
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1 text-xs font-bold text-white">
                    <ShieldCheck size={11} />
                    GDPR Compliant
                  </span>
                )}
              </div>
              <div className="mt-1 h-px bg-slate-200" />
              <div>
                {section.content.map((block, idx) => renderContent(block, idx))}
              </div>
            </section>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-16 rounded-lg border border-slate-200 bg-slate-50 px-5 py-5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-600" />
            <div>
              <div className="font-display text-sm font-bold text-slate-950">
                Questions about this Privacy Policy?
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                For any data protection questions, GDPR subject access requests, or to report a concern, email{" "}
                <a href="mailto:contact@hoardexa.com" className="font-semibold text-blue-600 transition hover:text-blue-700 hover:underline">
                  contact@hoardexa.com
                </a>
                . We respond to all requests within 30 days.
              </p>
            </div>
          </div>
        </div>
      </div>

      <FooterSimple />
    </div>
  );
}
